import { GridSize, makeStyles, Grid as MuiGrid, GridProps as MuiGridProps } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, forwardRef } from 'react';
export type { GridSize };

// Guideline to use Grid:
// https://css-tricks.com/snippets/css/complete-guide-grid/
// What it basically means is that you can use the "grid" prop to enable grid display
// then you can use "gridTemplateColumns" and "gridTemplateRows" in the style prop to define the structure
// It translate one on one with the xs prop of the material ui grid if you use "gridTemplateColumns: '1fr 2fr 1fr' "
// where the first and the last take 1/4 of the space and the middle one 2/4 of the space

const useStyles = makeStyles({
  flex: {
    display: 'flex',
  },
  grid: {
    display: 'grid',
  },
  gridTemplateColumns: ({ gridTemplateColumns }: { gridTemplateColumns?: number }) => ({
    gridTemplateColumns: `repeat(${gridTemplateColumns}, 1fr)`,
  }),
  row: {
    flexDirection: 'row',
  },
  column: {
    flexDirection: 'column',
  },
});

const getGridClassName = (
  classes: ReturnType<typeof useStyles>,
  { grid, row, column }: Pick<GridProps, 'grid' | 'row' | 'column'>,
  gridTemplateColumns?: number
) => {
  if (grid) {
    if (gridTemplateColumns) {
      return clsx(classes.grid, classes.gridTemplateColumns);
    }
    return classes.grid;
  }
  if (row) {
    return clsx(classes.row, classes.flex);
  }
  if (column) {
    return clsx(classes.column, classes.flex);
  }
  return '';
};

interface GridProps extends Omit<MuiGridProps, 'children'> {
  children?: React.ReactNode;
  grid?: boolean;
  row?: boolean;
  column?: boolean;
  gridTemplateColumns?: number;
  style?: CSSProperties;
}

export const Grid = forwardRef<HTMLDivElement, GridProps>(
  ({ children, row, column, grid, gridTemplateColumns, style, ...rest }, ref) => {
    const classes = useStyles({ gridTemplateColumns });
    const className = getGridClassName(classes, { grid, row, column }, gridTemplateColumns);

    return (
      <MuiGrid ref={ref} className={className} style={{ ...style }} {...rest}>
        {children}
      </MuiGrid>
    );
  }
);

Grid.displayName = 'Grid';
